import ChildrensTooltip from '@/components/common/ChildrensTooltip';
import Flex from '@/components/common/Flex';
import IconAlert from '@/components/common/IconAlert';
import DataLoadError from '@/components/errors/DataLoadError';
import { getActiveQueryError } from '@/app/common/utils/apiErrorUtils';
import { useCheckRemainingQuery } from '@/app/common/api/accessLevelApi';
import { useGetUserInfoQuery } from '@/app/common/api/authApi';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import classNames from 'classnames';
import { useState } from 'react';
import { useEffect } from 'react';
import { Button } from 'react-bootstrap';
import { Link } from 'react-router-dom';
import type { ReactNode } from 'react';
import useDomain from '@/hooks/useDomain';

type ExpireCheckProps = {
  children?: ReactNode;
  bypass?: boolean;
};

const ExpireCheck = ({ children, bypass = false }: ExpireCheckProps) => {
  const { data: userInfo } = useGetUserInfoQuery();
  const { domain_slug } = useDomain();

  const [showRemaining, setShowRemaining] = useState(true);
  const {
    data: remainingData,
    isFetching,
    isError,
    error,
    refetch,
  } = useCheckRemainingQuery(domain_slug, {
    skip: !domain_slug || domain_slug === 'no-domain' || !userInfo
  });
  useEffect(() => {
    setShowRemaining(true);
  }, [domain_slug]);
  const isPlanFinished =
    remainingData?.data?.remaining_days_from_plan === 0 &&
    remainingData?.data?.remaining_hours_from_plan === 0 &&
    remainingData?.data?.remaining_minutes_from_plan === 0 &&
    remainingData?.data?.remaining_seconds_from_plan === 0;
  const remainingDays = remainingData?.data?.remaining_days_from_plan;
  const accessLevel = (
    remainingData?.data as
      | { access_level?: number }
      | undefined
  )?.access_level;
  const queryError = getActiveQueryError([
    { isError, error, refetch, isFetching },
  ]);

  if (queryError) return <DataLoadError {...queryError} />;
  if (bypass) return children;
  return (
    !isFetching && (
      <>
        {showRemaining &&
          remainingDays !== undefined &&
          remainingDays < 8 && (
          <IconAlert
            onClose={() => setShowRemaining(false)}
            iconClasses="fs-0"
            customIcon="shopping-cart"
            dismissible={!isPlanFinished}
            variant={isPlanFinished ? 'danger' : 'warning'}
          >
            <div className="d-flex flex-column flex-md-row gap-2 justify-content-between align-items-start align-items-md-center">
              <p className="mb-0 text-justify pe-3">
                {isPlanFinished ? (
                  <>
                    <span>
                      <span className="mb-2 d-block">
                        طرح شما به پایان رسیده، اما مسیر رشد و موفقیت سایت شما
                        هنوز ادامه داره!
                      </span>
                      با تمدید اشتراک خود، می‌تونید دوباره به تمام ابزارهای
                      حرفه‌ای دسترسی داشته باشید، همراه شما هستیم تا به نتایج
                      بهتر برسید :)
                    </span>
                  </>
                ) : (
                  <>
                    طرح شما تا{' '}
                    {remainingDays > 0
                      ? `${remainingDays} روز `
                      : `${remainingData?.data?.remaining_hours_from_plan} ساعت و ${remainingData?.data?.remaining_minutes_from_plan} دقیقه `}
                    دیگه به پایان می‌رسه. لطفا برای تمدید، از طریق اکانت مالک
                    اقدام کن.
                  </>
                )}
              </p>
              <Flex className="gap-2 align-self-end align-self-md-auto me-2">
                <ChildrensTooltip
                  html={
                    accessLevel !== 1
                      ? 'خرید طرح دامنه فقط توسط مالک انجام شود.'
                      : ''
                  }
                  placement="top"
                >
                  <Link
                    to="/prorogate-or-upgrade/"
                    className={classNames('text-decoration-none', {
                      'disabled-link':
                        isFetching || accessLevel !== 1
                    })}
                  >
                    <Button
                      size="sm"
                      className="d-flex align-items-center gap-2"
                      variant="falcon-primary"
                    >
                      <FontAwesomeIcon icon="level-up" />
                      <span>
                        تغییر
                        <span className="d-md-inline d-none">&nbsp;طرح</span>
                      </span>
                    </Button>
                  </Link>
                </ChildrensTooltip>
              </Flex>
            </div>
          </IconAlert>
        )}
        {!isPlanFinished && children}
      </>
    )
  );
};

export default ExpireCheck;
